import { resolveProjectPlacement } from './agent-runtime-contract.js';
export const FOUNDRY_SETTING_IDS = [
    'github-credential',
    'anthropic-credential',
    'fal-credential',
    'registry-credentials',
    'games-folder',
    'github-organisation',
    'theme',
    'show-test-games',
    'live-preview-auto-open',
    'design-workspace',
    'tier',
    'build-model',
    'component-template-status',
    'log-level',
    'build-telemetry',
    'preview-phone-cap',
    'agent-ceilings',
    'dev-ports',
    'furnace-status',
    'analytics',
    'auto-updates',
    'devices',
    'diagnostics',
];
const SETTING_DEFINITIONS = [
    {
        id: 'github-credential',
        section: 'connections',
        label: 'GitHub',
        control: 'credential',
        capability: 'credentials',
        supportedAvailability: 'editable',
    },
    {
        id: 'anthropic-credential',
        section: 'connections',
        label: 'Anthropic',
        control: 'credential',
        capability: 'credentials',
        supportedAvailability: 'editable',
    },
    {
        id: 'fal-credential',
        section: 'connections',
        label: 'Fal.ai',
        control: 'credential',
        capability: 'credentials',
        supportedAvailability: 'editable',
    },
    {
        id: 'registry-credentials',
        section: 'connections',
        label: 'Registry tokens',
        control: 'credential',
        capability: 'credentials',
        supportedAvailability: 'editable',
    },
    {
        id: 'games-folder',
        section: 'preferences',
        label: 'Games folder',
        control: 'text',
        capability: 'localWorkspace',
        supportedAvailability: 'editable',
    },
    {
        id: 'github-organisation',
        section: 'preferences',
        label: 'GitHub organisation',
        control: 'text',
        capability: 'localWorkspace',
        supportedAvailability: 'editable',
    },
    {
        id: 'theme',
        section: 'preferences',
        label: 'Theme',
        control: 'select',
        capability: 'durableConfig',
        supportedAvailability: 'editable',
    },
    {
        id: 'show-test-games',
        section: 'preferences',
        label: 'Show test games',
        control: 'toggle',
        capability: 'durableConfig',
        supportedAvailability: 'editable',
    },
    {
        id: 'live-preview-auto-open',
        section: 'preferences',
        label: 'Open preview automatically during builds',
        control: 'toggle',
        capability: 'livePreviewPreference',
        supportedAvailability: 'editable',
    },
    {
        id: 'design-workspace',
        section: 'preferences',
        label: 'Enable Design workspace',
        control: 'toggle',
        capability: 'designPreference',
        supportedAvailability: 'editable',
    },
    {
        id: 'tier',
        section: 'preferences',
        label: 'Prosumer tools',
        control: 'toggle',
        capability: 'durableConfig',
        supportedAvailability: 'editable',
    },
    {
        id: 'build-model',
        section: 'advanced',
        label: 'Build model',
        control: 'select',
        capability: 'durableConfig',
        supportedAvailability: 'editable',
    },
    {
        id: 'component-template-status',
        section: 'advanced',
        label: 'Component template (Ingot system)',
        control: 'status',
        capability: 'componentTemplateStatus',
        supportedAvailability: 'read-only',
    },
    {
        id: 'log-level',
        section: 'advanced',
        label: 'Log level',
        control: 'select',
        capability: 'durableConfig',
        supportedAvailability: 'editable',
    },
    {
        id: 'build-telemetry',
        section: 'advanced',
        label: 'Send anonymous build-game telemetry',
        control: 'toggle',
        capability: 'durableConfig',
        supportedAvailability: 'editable',
    },
    {
        id: 'preview-phone-cap',
        section: 'advanced',
        label: 'Preview phone cap',
        control: 'select',
        capability: 'localPreviewDevices',
        supportedAvailability: 'editable',
    },
    {
        id: 'agent-ceilings',
        section: 'advanced',
        label: 'Per-message agent ceilings',
        control: 'number',
        capability: 'durableConfig',
        supportedAvailability: 'editable',
    },
    {
        id: 'dev-ports',
        section: 'advanced',
        label: 'Dev ports',
        control: 'number',
        capability: 'localWorkspace',
        supportedAvailability: 'editable',
    },
    {
        id: 'furnace-status',
        section: 'advanced',
        label: 'Furnace status',
        control: 'status',
        capability: 'furnaceStatus',
        supportedAvailability: 'read-only',
    },
    {
        id: 'analytics',
        section: 'privacy',
        label: 'Send anonymous analytics',
        control: 'toggle',
        capability: 'analyticsPreference',
        supportedAvailability: 'editable',
    },
    {
        id: 'auto-updates',
        section: 'advanced',
        label: 'Foundry updates',
        control: 'action',
        capability: 'autoUpdater',
        supportedAvailability: 'editable',
    },
    {
        id: 'devices',
        section: 'devices',
        label: 'Devices',
        control: 'action',
        capability: 'localPreviewDevices',
        supportedAvailability: 'editable',
    },
    {
        id: 'diagnostics',
        section: 'diagnostics',
        label: 'Diagnostics',
        control: 'action',
        capability: 'diagnostics',
        supportedAvailability: 'read-only',
    },
];
const LOCAL_ONLY_REASONS = {
    'github-credential': 'Credentials are managed by this Mac and are never exposed to Cloud projects.',
    'anthropic-credential': 'Credentials are managed by this Mac and are never exposed to Cloud projects.',
    'fal-credential': 'Credentials are managed by this Mac and are never exposed to Cloud projects.',
    'registry-credentials': 'Registry credentials are managed by this Mac and are never exposed to Cloud projects.',
    'games-folder': 'Cloud projects use a Cloud-authoritative workspace and do not have a local games folder.',
    'github-organisation': 'Cloud Git destinations are project-pinned and cannot be changed from Settings.',
    'preview-phone-cap': 'Preview phone simulation is currently available only on this Mac.',
    'dev-ports': 'Cloud preview networking is managed by Furnace and does not use local dev ports.',
    'auto-updates': 'The browser product is updated by the service; app updates are managed by Foundry Desktop.',
    devices: "Local device discovery requires Foundry Desktop and access to this Mac's network.",
};
const CLOUD_PERSISTENCE_REASON = 'Cloud account settings persistence is not available yet. This option remains visible but cannot be changed here.';
const CLOUD_CAPABILITY_REASONS = {
    durableConfig: CLOUD_PERSISTENCE_REASON,
    credentials: 'Credentials are managed by Foundry Desktop and are not exposed to Cloud projects.',
    localWorkspace: 'This option requires access to a local workspace in Foundry Desktop.',
    livePreviewPreference: 'Automatic preview opening is managed by Foundry Desktop and is not available in Cloud.',
    designPreference: 'The Design workspace is currently available only in Foundry Desktop.',
    componentTemplateStatus: 'Component template status reflects the local component template installation in Foundry Desktop.',
    localPreviewDevices: 'Local preview devices require Foundry Desktop and access to this Mac network.',
    furnaceStatus: 'Furnace status is unavailable for this Cloud project.',
    analyticsPreference: 'The Cloud service manages its analytics preference separately from Foundry Desktop.',
    autoUpdater: 'The browser product is updated by the service; app updates are managed by Foundry Desktop.',
    diagnostics: 'Diagnostics require access to local app and system state and are available in Foundry Desktop.',
};
/**
 * Explains why a setting capability is unavailable in the active host.
 *
 * Args:
 *   id: Stable identifier for the unavailable setting.
 *   capability: Host capability required by the setting.
 *   placement: Runtime placement where the setting is being displayed.
 *
 * Returns:
 *   A user-facing explanation specific to the setting or capability.
 *
 * Raises:
 *   None.
 */
function unavailableReason(id, capability, placement) {
    if (placement === 'cloud') {
        return LOCAL_ONLY_REASONS[id] ?? CLOUD_CAPABILITY_REASONS[capability];
    }
    return 'This option is disabled by the current Foundry runtime policy.';
}
export function buildResolvedFoundrySettings(options) {
    const placement = resolveProjectPlacement(options.placement);
    const settings = SETTING_DEFINITIONS.map((definition) => {
        const supported = options.capabilities[definition.capability];
        return Object.freeze({
            id: definition.id,
            section: definition.section,
            label: definition.label,
            control: definition.control,
            availability: supported ? definition.supportedAvailability : 'unavailable',
            unavailableReason: supported
                ? null
                : unavailableReason(definition.id, definition.capability, placement),
        });
    });
    return Object.freeze({
        version: 1,
        placement,
        settings: Object.freeze(settings),
    });
}
export function localFoundrySettingsCapabilities(options) {
    return Object.freeze({
        durableConfig: true,
        credentials: true,
        localWorkspace: true,
        livePreviewPreference: options?.livePreviewPreference ?? true,
        designPreference: options?.designPreference ?? true,
        componentTemplateStatus: options?.componentTemplateStatus ?? true,
        localPreviewDevices: true,
        furnaceStatus: true,
        analyticsPreference: true,
        autoUpdater: true,
        diagnostics: true,
    });
}
export function cloudFoundrySettingsCapabilities() {
    return Object.freeze({
        durableConfig: false,
        credentials: false,
        localWorkspace: false,
        livePreviewPreference: false,
        designPreference: false,
        componentTemplateStatus: false,
        localPreviewDevices: false,
        furnaceStatus: true,
        analyticsPreference: false,
        autoUpdater: false,
        diagnostics: false,
    });
}
//# sourceMappingURL=resolved-settings.js.map